import { intervalToDuration } from "date-fns";
import { Clock } from "lucide-react";
import { useState } from "react";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import { type ClaimTypeName, isCancellationLikeClaimType } from "../lib/claim-types";
import { formatInTimezone } from "../lib/dates";

interface Airport {
  timezone: string;
}

interface FlightOverviewScheduleProps {
  claimTypeName: ClaimTypeName | null;
  monitoringStatus: string | null;
  scheduledDepartureUTC: Date;
  scheduledArrivalUTC: Date;
  actualDepartureUTC?: Date | null;
  inGateArrivalUTC?: Date | null;
  departureAirport: Airport;
  arrivalAirport: Airport;
  utc: boolean;
}

/** Port of FlightOverviewSchedule.vue. */
export function FlightOverviewSchedule(props: FlightOverviewScheduleProps) {
  const [displayUTC, setDisplayUTC] = useState(false);

  function formatFlightSchedule(date: Date, timezone: string) {
    const tz = displayUTC ? "UTC" : timezone;
    return formatInTimezone(date, tz, "HH:mm ZZZZ");
  }

  function formatFlightDate(date: Date, timezone: string) {
    const tz = displayUTC ? "UTC" : timezone;
    return formatInTimezone(date, tz, "dd/MM/yyyy");
  }

  const isCancelled = props.monitoringStatus?.startsWith("CANCEL") ?? false;

  const departureTimeExpected = formatFlightSchedule(
    props.scheduledDepartureUTC,
    props.departureAirport.timezone,
  );
  const departureTimeActual = isCancelled
    ? "ANNULÉ"
    : !props.actualDepartureUTC
      ? "(aucune donnée)"
      : formatFlightSchedule(props.actualDepartureUTC, props.departureAirport.timezone);

  const arrivalTimeExpected = formatFlightSchedule(props.scheduledArrivalUTC, props.arrivalAirport.timezone);
  const arrivalTimeActual = isCancelled
    ? "ANNULÉ"
    : !props.inGateArrivalUTC
      ? "(aucune donnée)"
      : formatFlightSchedule(props.inGateArrivalUTC, props.arrivalAirport.timezone);

  const isLate = props.inGateArrivalUTC
    ? new Date(props.scheduledArrivalUTC).getTime() - new Date(props.inGateArrivalUTC).getTime() < 0
    : false;

  const interval = props.inGateArrivalUTC
    ? (() => {
        const parsedDelay = intervalToDuration({
          start: new Date(props.scheduledArrivalUTC),
          end: new Date(props.inGateArrivalUTC),
        });
        if (parsedDelay.days) {
          parsedDelay.hours ??= 0;
          parsedDelay.hours += parsedDelay.days * 24;
          parsedDelay.days = 0;
        }
        return parsedDelay;
      })()
    : null;

  const showDelayBadge = Boolean(
    interval?.minutes && (interval.minutes > 15 || interval.hours || interval.days) && props.inGateArrivalUTC,
  );

  const accentClass = isCancellationLikeClaimType(props.claimTypeName)
    ? "border-l-2 border-red-600"
    : props.claimTypeName
      ? "border-l-2 border-amber-500"
      : "border-l-2 border-blue-500";

  return (
    <div className={`pt-2 ${accentClass}`}>
      {/* Horaires prévus */}
      <div>
        <div className="ml-4 flex flex-row">
          <p className="text-base font-medium text-zinc-800">
            Horaires prévus {displayUTC ? "UTC" : ""} :{" "}
            <button type="button" className="text-xs" onClick={() => setDisplayUTC(!displayUTC)}>
              (<span className="underline">{displayUTC ? "voir en local" : "voir en UTC"}</span>)
            </button>
          </p>
        </div>

        <div className="flex flex-row pb-2">
          <div className="flex-2">
            <div className="flex flex-row">
              <div className="mt-2 ml-4 flex flex-col items-start">
                <p className="my-1 text-sm">Départ:</p>
              </div>
              <div className="mt-2 ml-2 flex flex-col items-start">
                <p className="text-sm leading-7 font-semibold">
                  {departureTimeExpected}{" "}
                  <span className="font-normal text-gray-700">
                    le {formatFlightDate(props.scheduledDepartureUTC, props.departureAirport.timezone)}
                  </span>
                </p>
              </div>
            </div>
          </div>
          <div className="flex-2">
            <div className="flex flex-row pb-2">
              <div className="mt-2 ml-4 flex flex-col items-start">
                <p className="my-1 text-sm">Arrivée:</p>
              </div>
              <div className="mt-2 ml-2 flex flex-col items-start">
                <p className="text-sm leading-7 font-semibold">
                  {arrivalTimeExpected}{" "}
                  <span className="font-normal text-gray-700">
                    le {formatFlightDate(props.scheduledArrivalUTC, props.arrivalAirport.timezone)}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Horaires réels */}
      <div>
        <div className="mb-1 ml-4 flex flex-row">
          <Tooltip>
            <TooltipTrigger
              render={<p className="inline-flex cursor-help items-center font-medium text-zinc-800" />}
            >
              <Clock className="mr-1 h-4 w-4 shrink-0 text-gray-400" />
              <span className="text-base">Horaires réels {displayUTC ? "UTC" : ""} : </span>
            </TooltipTrigger>
            <TooltipContent side="right">Via la surveillance des vols</TooltipContent>
          </Tooltip>
        </div>

        <div className="flex flex-row pb-2">
          <div className="flex-2">
            <div className="flex flex-row">
              <div className="mt-2 ml-4 flex flex-col items-start">
                <p className="my-1 text-sm">Départ:</p>
              </div>
              <div className="mt-2 ml-2 flex flex-col items-start">
                <p className="text-sm leading-7 font-semibold">
                  {props.monitoringStatus === "CANCELLED" ? (
                    <span className="font-mono">annulé</span>
                  ) : (
                    <span>
                      {departureTimeActual}{" "}
                      {props.actualDepartureUTC ? (
                        <span className="font-normal text-gray-700">
                          le {formatFlightDate(props.actualDepartureUTC, props.departureAirport.timezone)}
                        </span>
                      ) : null}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
          <div className="flex-2">
            <div className="flex flex-row pb-2">
              <div className="mt-2 ml-4 flex flex-col items-start">
                <p className="my-1 text-sm">Arrivée:</p>
              </div>
              <div className="mt-2 ml-2 flex flex-col items-start">
                <p className="text-left text-sm leading-7 font-semibold">
                  {props.monitoringStatus === "CANCELLED" ? (
                    <span className="font-mono">annulé</span>
                  ) : (
                    <span>{arrivalTimeActual}</span>
                  )}{" "}
                  {props.inGateArrivalUTC && props.monitoringStatus !== "CANCELLED" ? (
                    <span className="font-normal text-gray-700">
                      le {formatFlightDate(props.inGateArrivalUTC, props.arrivalAirport.timezone)}
                    </span>
                  ) : null}
                  {showDelayBadge && interval ? (
                    <span
                      className={`ml-1 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        isLate ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"
                      }`}
                    >
                      {isLate ? "Retardé de " : "En avance de "}
                      {interval.hours ? `${interval.hours} heures ` : ""}
                      {interval.minutes} min
                    </span>
                  ) : null}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
